import {
  Component, ComponentInterface, h, Prop, State, Watch, Event, EventEmitter,
  Fragment,
} from '@stencil/core';
import { ExpAPI } from '@pes-ui/components/dist/types/global-types/api/exp-api';
import { TStatus } from '@pes-ui/components/dist/types/components/pes-app-pdp/types';
import type { IProductsCarouselData } from '@pes-ui/components/dist/types/components/products-carousel/types';
import {
  TCustomPrice,
  TProductCtas,
  buildLocaleCode,
  send,
  SprykerProductsAll,
  buildPageViewPayload,
  deepMerge,
  ConfiguratorHandler,
  setupMedalliaSurvey,
  NetworkResponse, FeatureStatus, PageNames, pesCatalogPageTypes, ProductsApi, FeatureAttributes } from '@pes-ui/utils';

import type { DDCProps } from '@pes-ui/components/dist/types/globals/ddc';
import PesRoute from '@pes-ui/components/dist/collection/components/pes-router/pes-route';

import { cloneDeep } from 'lodash';
import { RecentlyViewService } from '../../../services/recently-viewed';
import { expAPIEndpoint, getExpAPIEndpointURL } from '../../../globals/utils/exp-api-urls-builder';
import { getData } from '../../../globals/utils';
import { MY_SCHNEIDER_CLICK_EVENT } from '../../../globals/constants';

const defaultStatus: TStatus = {
  isLoading: {
    all: true,
    ddc: true,
  },
  error: {
    all: false,
    ddc: false,
  },
};

/**
 * @slot discounted-price - Slot for discounted price. Will be rendered above CTA block.
 */
@Component({
  tag: 'pes-app-spryker-pdp-container',
  styleUrl: 'pes-app-spryker-pdp-container.scss',
  shadow: true,
})
export class PesAppPdpSprykerContainer implements ComponentInterface {
  /**
   * The host of the endpoints.
   * E.g.: https://www.se.com:9090
   */
  @Prop() host!: string;

  /**
   * The code of the country of the app.
   * E.g.: fr
   */
  @Prop() countryCode!: string;

  /**
   * The code of the language of the app.
   * E.g.: fr
   */
  @Prop() languageCode!: string;

  /**
   * The ID of the product.
   */
  @Prop() productId!: string;

  /**
   * Tab id for scroll to a registered tab.
   * @example documents
   */
  @Prop() tabId?: string;

  /**
   * Setup productCtas logic.
   * buttons - predefined CTA buttons.
   * productProp - predefined property of product to find in list.
   */
  @Prop() productCtas: TProductCtas;

  /**
   * Defines whether to show the breadcrumbs.
   */
  @Prop() showBreadcrumbs: boolean;

  /**
   * Show custom price.
   */
  @Prop() showCustomPrice = false;

  /**
   * Show PES price.
   */
  @Prop() showCatalogPrice = false;

  /**
   * Show buy online button
   */
  @Prop() showBuyOnline!: boolean;

  /**
   * Show buy online button
   */
  @Prop() showCompare!: boolean;

  /**
   * Show FAQ section
   */
  @Prop() showFaq!: boolean;

  /**
   * Show Recently Viewed section
   */
  @Prop() showRecentlyViewed!: boolean;

  /**
   * Defines whether Commerce Connector is enabled
   */
  @Prop() enableCommerceConnector?: boolean;

  /**
   * Custom price data.
   */
  @Prop() customPrice?: TCustomPrice;

  /**
   * This parameter contains configurations for the document listing component.
   * For more information, please visit
   * https://schneider.atlassian.net/wiki/spaces/DCP/pages/2137194972/Integration+Guideline+Document+Listing.
   */
  @Prop() documentListing: Partial<DDCProps>;

  @Prop() featureFlags: Record<string, FeatureStatus>;

  @State() status: TStatus = defaultStatus;

  /**
   * Maximum number recently viewed items to keep (default value is 16).
   */
  @State() maxRecentlyViewedItems = 16;

  @State() recentlyViewedProducts: IProductsCarouselData = {
    sectionTitle: '',
    data: [],
    versionLabel: '',
  };

  unifiedCheckoutFlag: FeatureAttributes = ConfiguratorHandler.getSingleConfig('FeatureFlags')?.unifiedCheckout;
  isUnifiedCheckoutEnabled = ConfiguratorHandler.isFeatureFlagEnabled(
    this.unifiedCheckoutFlag || {} as FeatureAttributes,
  );

  /**
   * Fired on navigation.
   * Contains page parameter as event.detail.
   */
  @Event({
    eventName: MY_SCHNEIDER_CLICK_EVENT,
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    pesLinkClick: EventEmitter<PesRoute>;

  emitPageViewEvent() {
    send(
      buildPageViewPayload,
      {
        pageLanguage: this.languageCode,
        pageTitle: this.allData.metaTags.analyticsPageData.pageTitle,
        fromWebView: this.allData.metaTags.analyticsPageData.fromWebView,
        businessUnit: this.allData.metaTags.analyticsPageData.businessUnit,
        template: this.allData.metaTags.analyticsPageData.template,
        pageTopCategory: this.allData.metaTags.analyticsPageData.pageTopCategory,
        pageCategory: this.allData.metaTags.analyticsPageData.pageCategory,
        pageSubCategory: this.allData.metaTags.analyticsPageData.pageSubCategory,
        pageSubSubCategory: this.allData.metaTags.analyticsPageData.pageSubSubCategory,
        pageCategoryId: this.allData.metaTags.analyticsPageData.pageCategoryId,
        pageSubCategoryId: this.allData.metaTags.analyticsPageData.pageSubCategoryId,
        pageRangeId: this.allData.metaTags.analyticsPageData.pageRangeId,
        pageProductId: this.allData.metaTags.analyticsPageData.pageProductId,
      },
    );
  }

  handleUnifiedCheckoutConfigs(): Promise<void> {
    if (!this.isUnifiedCheckoutEnabled) return;

    const currentApiConfig = ConfiguratorHandler.getSingleConfig('API');
    if (!currentApiConfig || !Array.isArray(currentApiConfig.list)) return;

    const updatedApiConfig = cloneDeep(currentApiConfig);

    const apiIndex = updatedApiConfig.list.findIndex((api) => api.name === 'spryker-products-all');

    if (apiIndex !== -1) {
      updatedApiConfig.list[apiIndex].version = '5.0';
    }
    ConfiguratorHandler.setSingleConfig('API', updatedApiConfig);
  }

  async setupPageForProduct(): Promise<void> {
    await this.loadData();
    if (this.allData?.metaTags?.analyticsPageData) this.emitPageViewEvent();

    if (!this.showRecentlyViewed) return;

    this.recentlyViewService.add(this.productId);
    this.requestRecentlyViewedProducts(this.productId);
  }

  async componentWillLoad(): Promise<void> {
    this.handleUnifiedCheckoutConfigs();
    await this.setupPageForProduct();
  }

  allData: ProductsApi.GetProductsAllResponse | null = null;

  private epdsData: ExpAPI.getMain['epds'] | null = null;

  private ddcData: ExpAPI.getDDCConfig | null = null;

  @Watch('productId')
  async optionDidChange(): Promise<void> {
    await this.setupPageForProduct();
  }

  recentlyViewService = (() => {
    if (!this.showRecentlyViewed) {
      return;
    }

    return RecentlyViewService.getInstance(
      getExpAPIEndpointURL(expAPIEndpoint.PRODUCT_CARD_SPRYKER),
      this.maxRecentlyViewedItems,
    );
  })();

  public getUpdatedStatus = (value) => JSON.parse(
    JSON.stringify(deepMerge({ ...this.status }, { ...value })),
  );

  public async requestRecentlyViewedProducts(productId: string): Promise<void> {
    try {
      const result = await this.recentlyViewService.getProducts(productId);
      if (!result) return;

      this.recentlyViewedProducts = {
        sectionTitle: this.allData?.recentlyViewedConfig?.sectionTitle,
        data: result,
        versionLabel: undefined, // not sent by backend
      };
    } catch (error) {
      console.error('[PES]: Could not fetch recently viewed products', error);
    }
  }

  public async fetchProductAll(): Promise<NetworkResponse<ProductsApi.GetProductsAllResponse>> {
    try {
      return await SprykerProductsAll.getProductsAll({
        productId: this.productId,
        languageCode: this.languageCode,
        countryCode: this.countryCode,
        brand: 'se',
        idmsToken: this.documentListing?.idmsToken,
      });
    } catch (error) {
      return { result: null, error };
    }
  }

  public async fetchEPDS() {
    try {
      const pdpRMEpdsEndpointURL = getExpAPIEndpointURL(expAPIEndpoint.RM_EPDS, {
        productId: this.productId,
      });

      return { result: await getData(false, pdpRMEpdsEndpointURL), error: null };
    } catch (error) {
      return { result: null, error };
    }
  }

  public async fetchDDC() {
    try {
      const ddcCommonEndpointURL = getExpAPIEndpointURL(expAPIEndpoint.DDC_COMMON_SPRYKER);

      return { result: await getData(false, ddcCommonEndpointURL), error: null };
    } catch (error) {
      return { result: null, error };
    }
  }

  public async loadData(): Promise<void> {
    if (!this.productId) return;

    this.status = this.getUpdatedStatus(defaultStatus);

    const { error: productAllError, result: productAllResult } = await this.fetchProductAll();

    if (productAllError) {
      console.error('Could not fetch Product All Data', productAllError);
      this.status = this.getUpdatedStatus(
        { isLoading: { all: false }, error: { all: { code: productAllError.message } } },
      );
      this.pesLinkClick.emit({ page: pesCatalogPageTypes.ALL_PRODUCTS });
      return;
    }

    this.allData = productAllResult;
    const rangeId = this.allData?.breadcrumbs?.find?.(
      (item) => item.itemType === PageNames.RANGE,
    )?.id;
    if (rangeId) setupMedalliaSurvey(rangeId);
    this.status = this.getUpdatedStatus({ isLoading: { all: false } });

    const { error: epdsError, result: epdsResult } = await this.fetchEPDS();

    if (epdsError) {
      console.error('Could not fetch EPDS Data', epdsError);
    } else {
      this.epdsData = epdsResult.epds;
    }

    const { error: ddcError, result: ddcResult } = await this.fetchDDC();

    if (ddcError) {
      console.error('Could not fetch DDC Data', ddcError);
      this.status = this.getUpdatedStatus(
        { isLoading: { ddc: false }, error: { ddc: { code: ddcError.code } } },
      );
      return;
    }

    this.ddcData = ddcResult;
    this.status = this.getUpdatedStatus({ isLoading: { ddc: false } });
  }

  render(): HTMLElement {
    return (
      <Fragment>
        <pes-app-spryker-pdp
          // params
          productId={this.productId}
          localeCode={this.allData?.base?.localeCode}
          countryCode={this.countryCode}
          productCtas={this.productCtas}
          showFaq={this.showFaq}

          // products/all request response data
          allData={this.allData}
          epdsData={this.epdsData}

          // requests status
          status={this.status}

          // customizations and settings
          isPBS={true}
          enableCommerceConnector={this.enableCommerceConnector}
          showBuyOnline={this.showBuyOnline}
          showBreadcrumbs={this.showBreadcrumbs}
          showCompare={this.showCompare}
          showCustomPrice={this.showCustomPrice}
          customPrice={this.customPrice}
          languageCode={this.languageCode}
          host={this.host}
          ddc={
            !this.status.isLoading.ddc && {
              documentLabel: this.ddcData.documentLabel,
              softwareAndFirmwareLabel: this.ddcData.softwareAndFirmwareLabel,
              productId: this.productId,
              apiEndpoint: this.ddcData.apiEndpoint,
              documentDetailsUrl: this.ddcData.documentDetailsUrl,
              localeCode: buildLocaleCode({
                countryCode: this.countryCode,
                languageCode: this.languageCode,
              }),
              idmsToken: this.documentListing?.idmsToken || '',
              myDocumentsAddedLabel: this.ddcData.myDocumentsAddedLabel,
              myDocumentsRemovedLabel: this.ddcData.myDocumentRemovedLabel,
              myDocumentsUrl: this.ddcData.myDocumentsUrl,
              myDocumentsCta: this.ddcData.myDocumentsCta,
              showMyDocuments: false,
              appSource: this.documentListing?.appSource || this.ddcData.appSource,
              userProfile: this.documentListing?.userProfile,
            }
          }
        >
          <slot name="discounted-price" slot="discounted-price" />
        </pes-app-spryker-pdp>
        <pes-products-carousel
          {...this.recentlyViewedProducts}
          hostWithLocale={`${this.host}${this.countryCode}/${this.languageCode}/`}
          pageType={PageNames.PRODUCT}
          pageId={this.productId}
          class="pes-app-pdp__section"
        ></pes-products-carousel>
      </Fragment>
    );
  }
}
